import { useEffect, useRef, useState } from 'react';
import { liveDraftKey, readLiveDraft, writeLiveDraft } from '../lib/liveDraftStorage';

export const useLiveDraft = ({ open, userId, liveId, initialForm }) => {
  const key = liveDraftKey(userId, liveId);
  const defaults = useRef(initialForm);
  defaults.current = initialForm;
  const current = useRef(initialForm);
  const ready = useRef(null);
  const baseline = useRef('');
  const source = useRef(null);
  const [form, setState] = useState(initialForm);
  const [hasDraft, setHasDraft] = useState(false);
  const [storageError, setStorageError] = useState(false);

  // Initialize once per opening/entity. Late feature config or catalog loads
  // must NEVER overwrite text already entered by the creator.
  useEffect(() => {
    ready.current = null;
    if (!open || !key) return;
    const initial = defaults.current;
    baseline.current = JSON.stringify(initial);
    source.current = liveId ? baseline.current : null;
    let restored = null;
    try { restored = readLiveDraft(key, initial, source.current); setStorageError(false); }
    catch (_) { setStorageError(true); }
    current.current = restored || initial;
    setState(current.current);
    setHasDraft(!!restored);
    ready.current = key;
    return () => { ready.current = null; };
  }, [open, key, liveId]);

  const setForm = (change) => {
    const next = typeof change === 'function' ? change(current.current) : change;
    current.current = next;
    setState(next);
    if (!open || ready.current !== key) return;
    const dirty = JSON.stringify(next) !== baseline.current;
    setHasDraft(dirty);
    try {
      if (dirty) writeLiveDraft(key, next, source.current);
      else localStorage.removeItem(key);
      setStorageError(false);
    } catch (_) { setStorageError(true); }
  };

  const clearDraft = () => {
    try { if (key) localStorage.removeItem(key); setStorageError(false); }
    catch (_) { setStorageError(true); }
    const initial = defaults.current;
    baseline.current = JSON.stringify(initial);
    source.current = liveId ? baseline.current : null;
    current.current = initial;
    setState(initial);
    setHasDraft(false);
  };
  return { form, setForm, clearDraft, hasDraft, storageError };
};